import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import handleError from "@/utils/handleError";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { User } from "snudbud-backbone-sdk";
import { useAPI } from "../contexts/APIContext";

export default function BookingPage() {
  const { userId, slotId } = useParams<{ userId: string; slotId: string }>();
  const { user, api } = useAPI();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [slot, setSlot] = useState<User.Schedule.QueryReturn["slots"][0]>();
  const [profileData, setProfileData] = useState<User.FetchProfileReturn>();
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [error, setError] = useState("");

  const isOwnProfile = user?.userId === userId;

  useEffect(() => {
    if (!userId || !slotId) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        // Fetch slot details
        const start = new Date(new Date().getTime() - 1000 * 60 * 60 * 2);
        const result = await api.user.schedule.query({ userId, start });

        const foundSlot = result.slots.find((s) => s.scheduleSlotId === slotId);

        if (!foundSlot) {
          setError("Availability slot not found or no longer available");
          setIsLoading(false);
          return;
        }

        setSlot(foundSlot);

        // Fetch profile data
        const profile = await api.user.fetchProfile({ userId });
        setProfileData(profile);
      } catch (err) {
        handleError(err);
        setError("Failed to load booking information");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [userId, slotId, api]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userId || !slotId || !user) {
      setError("Missing required information for booking");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await api.user.schedule.bookSlot({ message: notes, userId, scheduleSlotId: slotId });
      setBookingSuccess(true);
    } catch (err) {
      handleError(err);
      setError("Failed to book the session. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTimeSlot = (startsAt: Date, durationMinutes: number) => {
    try {
      const startDate = new Date(startsAt);
      const endDate = new Date(startDate.getTime() + durationMinutes * 60000);

      const formattedStart = format(startDate, "h:mm a");
      const formattedEnd = format(endDate, "h:mm a");
      return `${formattedStart} - ${formattedEnd}`;
    } catch (error) {
      return "Invalid time";
    }
  };

  const formatDate = (date: Date) => {
    return format(new Date(date), "EEEE, MMMM d, yyyy");
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-1 max-w-3xl mx-auto px-4 py-8 w-full">
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-pulse text-gray-500">Loading booking information...</div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl p-6 shadow">
            <div className="text-center py-4 text-red-500">{error}</div>
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => navigate(-1)}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        ) : bookingSuccess ? (
          <div className="bg-white rounded-2xl p-6 shadow text-center">
            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-2">Booking Confirmed!</h2>
            <p className="text-gray-600 mb-6">Your session has been successfully booked with {profileData?.name}.</p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => navigate(`/profile/${userId}`)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                View Profile
              </button>
              <button
                onClick={() => navigate("/dashboard")}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-6 shadow">
            <h1 className="text-2xl font-bold mb-6">Book a Session</h1>

            {slot && profileData && (
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <img
                    src={profileData.profileImageUrl || "https://via.placeholder.com/40"}
                    alt={profileData.name || "User"}
                    className="w-10 h-10 rounded-full mr-3"
                  />
                  <h2 className="text-lg font-semibold">{profileData.name}</h2>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <div className="text-gray-600 mb-1">Date</div>
                  <div className="text-gray-900 font-medium">{formatDate(new Date(slot.startsAt))}</div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <div className="text-gray-600 mb-1">Time</div>
                  <div className="text-gray-900 font-medium">
                    {formatTimeSlot(new Date(slot.startsAt), slot.duration)}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-gray-600 mb-1">Duration</div>
                  <div className="text-gray-900 font-medium">{slot.duration} minutes</div>
                </div>
              </div>
            )}

            {isOwnProfile ? (
              <div className="bg-yellow-50 border border-yellow-100 text-yellow-700 p-4 rounded-lg mb-6">
                You cannot book a session with yourself. This is your own availability slot.
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label htmlFor="notes" className="block text-gray-700 font-medium mb-2">
                    Notes (optional)
                  </label>
                  <textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Let them know why you'd like to meet and what you'd like to discuss..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px]"
                  ></textarea>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="mr-3 px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || isOwnProfile}
                    className={`px-4 py-2 rounded-lg text-white ${
                      isOwnProfile
                        ? "bg-gray-400 cursor-not-allowed"
                        : isSubmitting
                        ? "bg-blue-400"
                        : "bg-blue-600 hover:bg-blue-700"
                    } transition-colors`}
                  >
                    {isSubmitting ? "Booking..." : "Confirm Booking"}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
